import { useCallback, useEffect, useMemo, useState } from 'react'
import { useIntl } from 'react-intl'
import { useHistory, useLocation, useParams } from 'react-router-dom'
import ModalsComponent from '../stories/atoms/modals'
import { modalSizeOptions } from '../stories/atoms/modals/constants'
import { buttonVariantOptions } from '../stories/atoms/button/constants'
import { deserializeQueryString } from '../../utils/urlManager'
import { useGetProjectDetailsQuery } from '../../react-query/project/query/use-get-project-details-query'
import CopyProjectMainContent from './components/copy-project-main-content'
import CopyProjectMembersSelector from './components/copy-project-members-selector'
import { useCopyProjectMutation } from '../../react-query/project/mutation/use-copy-project-mutation'
import useMembersSelectionHook from './hooks/use-members-selection-hook'
import {
  cloneSelectionByTab,
  getSelectedItemIdsByTab,
  toggleSelectionItemInSelection
} from './hooks/members-selection-model'
import {
  copyProjectMembersSelectionOptions,
  copyProjectMembersTabOptions,
  copyProjectTeamGroupsSelectionModeOptions
} from './constants'
import { useSnackbar } from '../stories/molecules/snackbar/component/snackbar-hook'
import { snackbarVariantOptions } from '../stories/molecules/snackbar/constants'
import { useProjectMenuListViewControl } from '../../hooks/use-project-menu-list-view-control'
import {
  projectMenuApiErrorCodeOptions,
  projectMenuErrorMessages
} from '../../containers/project-menu/constants'

const defaultInclude = {
  categories: false,
  fields: false,
  datasets: false,
  templates: false
}

const normalizeCheckboxValue = (currentValue, nextValue) => {
  if (typeof nextValue === 'function') {
    return nextValue(currentValue)
  }

  return nextValue
}

const CopyProjectModal = () => {
  const { projectId } = useParams()
  const history = useHistory()
  const location = useLocation()
  const { formatMessage } = useIntl()
  const { addSnackbar } = useSnackbar()
  const { folderId = null } = deserializeQueryString(location.search)
  const defaultParentId = folderId ?? location.state?.parent?.id ?? null
  const { viewType, tab } = useProjectMenuListViewControl()

  const [projectNameHint, setProjectNameHint] = useState(null)
  const [projectCreationError, setProjectCreationError] = useState(null)

  const handleCancel = () => {
    history.push(`/project?view=${viewType}&tab=${tab}`)
  }

  const handleOnSuccess = () => {
    addSnackbar({
      message: formatMessage({ id: 'project_copied' }),
      snackbarType: snackbarVariantOptions.SUCCESS,
      hideCloseButton: true
    })
    handleCancel()
  }

  const handleOnError = (error) => {
    if (
      error &&
      error?.response?.data?.status ===
        projectMenuApiErrorCodeOptions.NO_PERMISSION_TO_CREATE_PROJECT_OR_FOLDER
    ) {
      return setProjectCreationError(
        formatMessage({
          id: 'projectMenu_no_permission_to_create'
        })
      )
    }

    if (
      error &&
      error.message === projectMenuErrorMessages.PROJECT_NAME_IS_NOT_AVAILABLE
    ) {
      return setProjectNameHint(
        formatMessage({
          id: 'projectMenu_project_with_this_name_aready_exists'
        })
      )
    }
  }

  const { data: projectData, isLoading } = useGetProjectDetailsQuery({
    projectId
  })

  const { mutate, isPending } = useCopyProjectMutation({
    onSuccess: handleOnSuccess,
    onError: handleOnError
  })

  const [name, setName] = useState('')

  const [parentId, setParentId] = useState(defaultParentId)
  const [include, setInclude] = useState(defaultInclude)

  const [isMembersSelectorOpen, setIsMembersSelectorOpen] = useState(false)
  const [draftSelectionByTab, setDraftSelectionByTab] = useState(null)

  const [membersSelectorTab, setMembersSelectorTab] = useState(
    copyProjectMembersTabOptions.MEMBERS
  )

  const {
    contacts,
    isLoadingContacts,
    isLoadingMembers,
    isLoadingTeams,
    isIncludedByTab,
    itemsByTab,
    members,
    selectedItemIdsByTab: appliedSelectedItemIdsByTab,
    selectionByTab: appliedSelectionByTab,
    replaceSelectionByTab,
    teams,
    toggleIncludeTeamGroupMembers,
    toggleIsIncludedByTab
  } = useMembersSelectionHook(projectId)

  const draftSelectedItemIdsByTab = useMemo(
    () =>
      getSelectedItemIdsByTab(
        draftSelectionByTab ?? appliedSelectionByTab,
        itemsByTab
      ),
    [appliedSelectionByTab, draftSelectionByTab, itemsByTab]
  )

  const isLoadingModal =
    isLoading ||
    isPending ||
    isLoadingMembers ||
    isLoadingTeams ||
    isLoadingContacts

  useEffect(() => {
    setName('')
    setParentId(defaultParentId)
    setInclude(defaultInclude)
    setDraftSelectionByTab(null)
    setIsMembersSelectorOpen(false)
    setMembersSelectorTab(copyProjectMembersTabOptions.MEMBERS)
  }, [projectId, defaultParentId])

  const handleNameChange = (nextName) => {
    setName(nextName)
  }

  const handleParentChange = (folder) => {
    setParentId(folder?.id ?? null)
  }

  const handleToggleInclude = (key, nextValue) => {
    const resolvedValue = normalizeCheckboxValue(include[key], nextValue)

    const nextInclude = {
      ...include,
      [key]: resolvedValue
    }

    if (key === 'fields' && resolvedValue) {
      nextInclude.categories = true
    }

    setInclude(nextInclude)
  }

  const handleOpenMembersSelector = (tab) => {
    setMembersSelectorTab(tab)
    setDraftSelectionByTab(cloneSelectionByTab(appliedSelectionByTab))
    setIsMembersSelectorOpen(true)
  }

  const handleCloseMembersSelector = () => {
    setDraftSelectionByTab(null)
    setIsMembersSelectorOpen(false)
  }

  const handleApplyMembersSelector = () => {
    replaceSelectionByTab(draftSelectionByTab ?? appliedSelectionByTab)
    setDraftSelectionByTab(null)
    setIsMembersSelectorOpen(false)
  }

  const handleToggleDraftSelectionItem = useCallback(
    (tab, itemId) => {
      setDraftSelectionByTab((currentDraftSelectionByTab) =>
        toggleSelectionItemInSelection(
          currentDraftSelectionByTab ?? appliedSelectionByTab,
          itemsByTab,
          tab,
          itemId
        )
      )
    },
    [appliedSelectionByTab, itemsByTab]
  )

  const handleToggleSelectionCheckbox = (tab, nextValue) => {
    toggleIsIncludedByTab(tab, nextValue)
  }

  const handleToggleIncludeTeamGroupMembers = (nextValue) => {
    toggleIncludeTeamGroupMembers(nextValue)
  }

  const getSelectionSummary = (tab) => {
    return appliedSelectionByTab?.[tab]?.scope ===
      copyProjectMembersSelectionOptions.ALL
      ? formatMessage({ id: 'projects_all' })
      : `${appliedSelectedItemIdsByTab?.[tab]?.length ?? 0}`
  }

  const selectionSummaryByTab = {
    [copyProjectMembersTabOptions.MEMBERS]: getSelectionSummary(
      copyProjectMembersTabOptions.MEMBERS
    ),
    [copyProjectMembersTabOptions.TEAMS]: getSelectionSummary(
      copyProjectMembersTabOptions.TEAMS
    ),
    [copyProjectMembersTabOptions.CONTACTS]: getSelectionSummary(
      copyProjectMembersTabOptions.CONTACTS
    )
  }

  const hasSelectableItemsByTab = {
    [copyProjectMembersTabOptions.MEMBERS]:
      itemsByTab[copyProjectMembersTabOptions.MEMBERS].length > 0,
    [copyProjectMembersTabOptions.TEAMS]:
      itemsByTab[copyProjectMembersTabOptions.TEAMS].length > 0,
    [copyProjectMembersTabOptions.CONTACTS]:
      itemsByTab[copyProjectMembersTabOptions.CONTACTS].length > 0
  }

  const hasSelectedCopyOptions =
    Object.values(include).some(Boolean) ||
    Object.values(isIncludedByTab).some(Boolean)

  const handleSubmit = () => {
    if (isSubmitDisabled) return null
    setProjectNameHint(null)
    setProjectCreationError(null)

    return mutate({
      body: buildCopyProjectPayload()
    })
  }

  const buildCopyProjectPayload = () => {
    const membersSelection =
      appliedSelectionByTab[copyProjectMembersTabOptions.MEMBERS]
    const teamsSelection =
      appliedSelectionByTab[copyProjectMembersTabOptions.TEAMS]
    const contactsSelection =
      appliedSelectionByTab[copyProjectMembersTabOptions.CONTACTS]

    const hasSelectedTeamGroups =
      isIncludedByTab[copyProjectMembersTabOptions.TEAMS] &&
      teamsSelection?.scope !== copyProjectMembersSelectionOptions.NONE &&
      teamsSelection?.scope !== copyProjectMembersSelectionOptions.EMPTY

    const isTeamGroupsAllSelected =
      teamsSelection?.scope === copyProjectMembersSelectionOptions.ALL

    const isTeamGroupsCustomSelection =
      teamsSelection?.scope === copyProjectMembersSelectionOptions.CUSTOM

    const shouldExcludeListedTeamGroups =
      teamsSelection?.teamGroupsMode ===
      copyProjectTeamGroupsSelectionModeOptions.EXCLUDE

    const excludeTeamGroups =
      hasSelectedTeamGroups &&
      (isTeamGroupsAllSelected || shouldExcludeListedTeamGroups)

    const teamGroupIds =
      hasSelectedTeamGroups && isTeamGroupsCustomSelection
        ? excludeTeamGroups
          ? (teamsSelection?.selectedIds ?? [])
          : appliedSelectedItemIdsByTab[copyProjectMembersTabOptions.TEAMS]
        : []

    const includeTeamGroupMembers = hasSelectedTeamGroups
      ? !!teamsSelection?.includeTeamGroupMembers
      : false

    return {
      name: name.trim(),
      id: projectId,
      parentId,
      categories: include.categories,
      fields: include.fields,
      datasets: include.datasets,
      templates: include.templates,

      team:
        isIncludedByTab[copyProjectMembersTabOptions.MEMBERS] &&
        membersSelection?.scope === copyProjectMembersSelectionOptions.ALL,
      teamMembers:
        isIncludedByTab[copyProjectMembersTabOptions.MEMBERS] &&
        membersSelection?.scope === copyProjectMembersSelectionOptions.CUSTOM
          ? appliedSelectedItemIdsByTab[copyProjectMembersTabOptions.MEMBERS]
          : null,

      teamGroups: hasSelectedTeamGroups,
      excludeTeamGroups,
      includeTeamGroupMembers,
      teamGroupIds,

      contacts:
        isIncludedByTab[copyProjectMembersTabOptions.CONTACTS] &&
        contactsSelection?.scope === copyProjectMembersSelectionOptions.ALL,
      contactsList:
        isIncludedByTab[copyProjectMembersTabOptions.CONTACTS] &&
        contactsSelection?.scope === copyProjectMembersSelectionOptions.CUSTOM
          ? appliedSelectedItemIdsByTab[copyProjectMembersTabOptions.CONTACTS]
          : null
    }
  }

  const isSubmitDisabled =
    isLoadingModal || !name.trim() || !hasSelectedCopyOptions

  const rightButtons = isMembersSelectorOpen
    ? [
        {
          title: formatMessage({ id: 'back' }),
          variant: buttonVariantOptions['TERTIARY-HOLLOW'],
          action: handleCloseMembersSelector
        },
        {
          title: formatMessage({ id: 'apply' }),
          variant: buttonVariantOptions.PRIMARY,
          action: handleApplyMembersSelector,
          disabled: false
        }
      ]
    : [
        {
          title: formatMessage({ id: 'cancel' }),
          variant: buttonVariantOptions['TERTIARY-HOLLOW'],
          action: handleCancel
        },
        {
          title: formatMessage({ id: 'projects_copyProject' }),
          variant: buttonVariantOptions.PRIMARY,
          action: handleSubmit,
          isLoading: isLoadingModal,
          disabled: isSubmitDisabled
        }
      ]

  return (
    <ModalsComponent
      title={formatMessage({ id: 'projects_copyProject' })}
      size={modalSizeOptions.SMALL}
      rightButtons={rightButtons}
      isOpen={true}
      onClose={handleCancel}
      autoHeight={true}
    >
      {isMembersSelectorOpen ? (
        <CopyProjectMembersSelector
          defaultTab={membersSelectorTab}
          members={members}
          teams={teams}
          contacts={contacts}
          selectedItemIdsByTab={draftSelectedItemIdsByTab}
          onToggleSelectItem={handleToggleDraftSelectionItem}
          isLoadingMembers={isLoadingMembers}
          isLoadingTeams={isLoadingTeams}
          isLoadingContacts={isLoadingContacts}
        />
      ) : (
        <CopyProjectMainContent
          sourceProjectName={projectData?.name ?? location.state?.projectName}
          defaultFolderId={defaultParentId}
          value={{
            name,
            parentId,
            include
          }}
          summary={selectionSummaryByTab}
          checkboxState={isIncludedByTab}
          hasSelectableItemsByTab={hasSelectableItemsByTab}
          teamGroupsIncludeMembers={
            !!appliedSelectionByTab?.[copyProjectMembersTabOptions.TEAMS]
              ?.includeTeamGroupMembers
          }
          actions={{
            setName: handleNameChange,
            setParentId: handleParentChange,
            toggleInclude: handleToggleInclude,
            toggleSelectionCheckbox: handleToggleSelectionCheckbox,
            toggleIncludeTeamGroupMembers: handleToggleIncludeTeamGroupMembers,
            openMembersSelector: handleOpenMembersSelector
          }}
          isLoading={isLoadingModal}
          titleInputHint={projectNameHint}
          projectCreationError={projectCreationError}
        />
      )}
    </ModalsComponent>
  )
}

export default CopyProjectModal
